import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, CalendarDays, Landmark, Receipt, Settings2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

const ICONS = { tva: Receipt, urssaf: Users, impot: Landmark, cfe: Landmark };
const FIELDS = [["tva", "TVA"], ["urssaf", "URSSAF"], ["impot", "Impôt sur les bénéfices"]];

const FiscalSettings = ({ company, data, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [v, setV] = useState(data.settings);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/business/companies/${company.id}/fiscal`, v); toast.success("Calendrier fiscal mis à jour"); qc.invalidateQueries({ queryKey: ["biz-fiscal", company.id] }); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="fiscal-settings-dialog">
        <DialogHeader><DialogTitle>Votre régime fiscal et social</DialogTitle>
          <DialogDescription>Les échéances officielles standard sont calculées selon ces choix (décalées au jour ouvré suivant si week-end).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {FIELDS.map(([k, l]) => (
            <label key={k} className="block space-y-1 text-xs font-medium">{l}
              <Select value={v[k]} onValueChange={(x) => setV({ ...v, [k]: x })}><SelectTrigger className="rounded-xl" data-testid={`fiscal-${k}-select`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(data.options[k]).map(([o, t]) => <SelectItem key={o} value={o} data-testid={`fiscal-${k}-${o}`}>{t}</SelectItem>)}</SelectContent></Select></label>
          ))}
          <label className="flex items-center justify-between rounded-xl border border-border p-3 text-sm">CFE (cotisation foncière, 15 décembre)<Switch checked={v.cfe} onCheckedChange={(x) => setV({ ...v, cfe: x })} data-testid="fiscal-cfe-switch" /></label>
          <Button className="w-full rounded-full" disabled={busy || company.my_role === "viewer"} onClick={save} data-testid="fiscal-save">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ r }) => {
  const { date } = useFormat();
  const I = ICONS[r.kind] || CalendarDays;
  const soon = r.days_left <= 7;
  return (
    <li className={cn("flex items-center gap-3 rounded-xl border p-3 text-sm", soon ? "border-destructive/30 bg-destructive/5" : "border-border")} data-testid={`fiscal-deadline-${r.kind}-${r.date}`}>
      <I className={cn("h-4 w-4 shrink-0", soon ? "text-destructive" : "text-primary")} />
      <span className="min-w-0 flex-1"><b>{r.org}</b><span className="block text-xs text-muted-foreground">{r.label}</span></span>
      <span className="text-right"><span className="block text-xs font-semibold">{date(r.date)}</span>
        <span className={cn("num text-xs font-bold", soon ? "text-destructive" : "text-muted-foreground")}>{r.days_left === 0 ? "Aujourd'hui" : `J-${r.days_left}`}</span></span>
    </li>
  );
};

export const FiscalCalendar = ({ company }) => {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["biz-fiscal", company.id], queryFn: () => api.get(`/business/companies/${company.id}/fiscal`).then((r) => r.data) });
  if (!data) return null;
  const list = data.deadlines.slice(0, 6);
  return (
    <section className="card-surface space-y-3 p-5" data-testid="fiscal-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex flex-1 items-center gap-2 font-bold"><CalendarDays className="h-4 w-4" /> Calendrier fiscal et social</h2>
        {company.my_role !== "viewer" && <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setOpen(true)} data-testid="fiscal-settings-open"><Settings2 className="mr-1 h-3.5 w-3.5" /> {data.configured ? "Mon régime" : "Choisir mon régime"}</Button>}
      </div>
      {data.configured ? <ul className="space-y-2" data-testid="fiscal-deadlines">{list.map((r) => <Row key={r.key} r={r} />)}</ul>
        : <p className="text-sm text-muted-foreground" data-testid="fiscal-not-configured">Indiquez votre régime de TVA, URSSAF et impôt pour afficher vos prochaines échéances officielles.</p>}
      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><BellRing className="h-3.5 w-3.5" /> Rappel dans l'app {data.remind_days.join(" et ")} jours avant. Dates standard indicatives : vérifiez votre espace impots.gouv.fr et urssaf.fr.</p>
      {open && <FiscalSettings company={company} data={data} open={open} onOpenChange={setOpen} />}
    </section>
  );
};
